import React from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import RoleManagement from './pages/memberManagement/RoleManagement';
import TeamMeeting from './pages/onboarding/teamMeeting';
import SalesReport from './pages/reports/SalesReport';
import TeamMeetingReport from './pages/onboarding/TeamMeetingReport';
import TeamPhoneCall from './pages/onboarding/teamPhoneCalls';
import TeamPhoneCallsReport from './pages/onboarding/teamPhoneCallsReport';
import RmMagaineReport from './pages/reports/rmMagazineReport';
import RmMagaineDetails from './pages/reports/rmMagazineReport/MagazineDetails';
import CoachMaitenence from './pages/onboarding/CoachMaintenance';
import EditCoach from './pages/onboarding/CoachMaintenance/EditCoach';
import CreateCoach from './pages/onboarding/CoachMaintenance/CreateCoach';
import Offers from './pages/marketing/Offers';
import SingleOffer from './pages/marketing/SingleOffer';
import TripWireReport from './pages/reports/tripWireReport';
import EditOffer from './pages/marketing/EditOffer';
import CreateOffer from './pages/marketing/CreateOffer';
import Offboarding from './pages/v2/offboarding';
import OffboardingMember from './pages/Offboarding/OffboardingMember';
import LoginPage from '../LoginPage';
import OffboardingDetails from './pages/v2/offboarding/details';
import HomePage from './pages/HomePage';
import MagazineRecipient from './pages/reports/MagazineRecipient';
import UserLeads from './pages/reports/MagazineRecipient/UserLeads';
import ReferralMarketing from './pages/v2/referral-marketing';
import RmmAutomationDetails from './pages/v2/referral-marketing/components/RmmAutomationDetails';
import PrintShopQueue from './pages/printShop';
import PrintShopLegacyRedirect from './pages/printShop/LegacyRedirect';
import { LEGACY_PRINT_SHOP_DEFAULT_PATH } from './pages/printShop/queues';
import env from '../environments';
import PrintShopTicketDetail from './pages/printShop/TicketDetail';
import RmPrintQueue from './pages/printShop/RmQueue';
import RmJobDetail from './pages/printShop/RmQueue/JobDetail';
import PrintShopPricingConfig from './pages/printShop/PricingConfig';
import PrintShopBatchesList from './pages/printShop/Batches';
import PrintShopBatchBuilder from './pages/printShop/Batches/BatchBuilder';
import ProtectedRoute from './components/ProtectedRoute';
import { usePermissions } from './hooks/usePermissions';
import Performance from './pages/performance';
import CustomerInformation from './pages/customerInformation/CustomerInformation';
import CustomerDetail from './pages/customerInformation/CustomerDetail';
import MemberActivity from './pages/memberActivity';
import AccountManagers from './pages/accountManagers/AccountManagers';
import BookManagement from './pages/bookManagement/BookManagement';
import AssetManagement from './pages/assetManagement/AssetManagement';
import BookDrafts from './pages/bookDrafts/BookDrafts';
import MldList from './pages/mld';
import MldDetail from './pages/mld/MldDetail';
export function Callback() {
  return (
    <div className="flex items-center justify-center min-h-screen">
      <div className="text-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-500 mx-auto"></div>
        <p className="mt-4 text-lg">Processing login...</p>
      </div>
    </div>
  );
}

/** A hidden Mail on Demand page sends its URL to the Books queue. */
const HiddenMailOnDemandRedirect = () => (
  <Navigate to={LEGACY_PRINT_SHOP_DEFAULT_PATH} replace />
);

const RoutesConfig = () => {
  const {
    canAccessCoachMaintenance,
    canAccessRMM,
    canAccessPerformance,
    canAccessBookManagement,
  } = usePermissions();

  return (
    <Routes>
      <Route path="/" element={<HomePage />} />
      <Route path="/login" element={<LoginPage />} />
      {/* Password management consolidated into Customer Information > Security.
          Redirect kept so old bookmarks/runbooks/support links don't 404. */}
      <Route
        path="/member-password-change"
        element={<Navigate to="/customer-information" replace />}
      />
      <Route path="/customer-information" element={<CustomerInformation />} />
      <Route path="/customer-information/:id" element={<CustomerDetail />} />
      <Route path="/member-activity" element={<MemberActivity />} />
      <Route path="/account-managers" element={<AccountManagers />} />
      <Route
        path="/book-management"
        element={
          <ProtectedRoute checkAccess={canAccessBookManagement}>
            <BookManagement />
          </ProtectedRoute>
        }
      />
      <Route
        path="/asset-management"
        element={
          <ProtectedRoute checkAccess={canAccessBookManagement}>
            <AssetManagement />
          </ProtectedRoute>
        }
      />
      <Route path="/role-management" element={<RoleManagement />} />
      {/* Internal support tool (Sidebar: Members > Book Draft Switcher), gated by book_management. */}
      <Route
        path="/book-drafts"
        element={
          <ProtectedRoute checkAccess={canAccessBookManagement}>
            <BookDrafts />
          </ProtectedRoute>
        }
      />
      {/* Native Member List Deal screens (Sidebar: Members > Member List
          Deals), mounted only while VITE_ENABLE_MLD is on (ADR-007). Off,
          no /mld route exists at all, so nothing renders and no MLD request
          is made. Access is the existing ABO operator access: no new role. */}
      {env.ENABLE_MLD && (
        <>
          <Route path="/mld" element={<MldList />} />
          <Route path="/mld/:subscriptionId" element={<MldDetail />} />
        </>
      )}
      <Route path="/team-meetings" element={<TeamMeeting />} />
      <Route path="/sales-report" element={<SalesReport />} />
      <Route
        path="/team-meetings/:teamMemberEmail"
        element={<TeamMeetingReport />}
      />
      <Route path="/team-phone-calls" element={<TeamPhoneCall />} />
      <Route
        path="/team-phone-calls/:teamMemberEmail/:currToken?/:currPage?"
        element={<TeamPhoneCallsReport />}
      />
      <Route path="/tripwire-report" element={<TripWireReport />} />
      <Route path="/magazine-recipient" element={<MagazineRecipient />} />
      <Route path="/magazine-recipient/:userId" element={<UserLeads />} />
      <Route path="/rm-magazine-report" element={<RmMagaineReport />} />
      <Route
        path="/rm-magazine-report/details/:year?/:month?/:customerId?"
        element={<RmMagaineDetails />}
      />
      <Route
        path="/coach-maintenance"
        element={
          <ProtectedRoute checkAccess={canAccessCoachMaintenance}>
            <CoachMaitenence />
          </ProtectedRoute>
        }
      />
      <Route
        path="/coaches/edit/:id"
        element={
          <ProtectedRoute checkAccess={canAccessCoachMaintenance}>
            <EditCoach />
          </ProtectedRoute>
        }
      />
      <Route
        path="/coaches/create"
        element={
          <ProtectedRoute checkAccess={canAccessCoachMaintenance}>
            <CreateCoach />
          </ProtectedRoute>
        }
      />
      <Route path="/offers" element={<Offers />} />
      <Route path="/offers/new-offer" element={<CreateOffer />} />
      <Route path="/offers/new-offer/preview" element={<CreateOffer />} />
      <Route path="/offers/:offerCode/edit" element={<CreateOffer />} />
      <Route path="/offers/:offerCode" element={<SingleOffer />} />
      {/* <Route path="/editoffers/:id" element={<EditOffer />} /> */}
      <Route
        path="/performance"
        element={
          <ProtectedRoute checkAccess={canAccessPerformance}>
            <Performance />
          </ProtectedRoute>
        }
      />
      <Route path="/offboarding" element={<Offboarding />} />
      <Route path="/offboarding/:id" element={<OffboardingDetails />} />
      <Route path="/app/callback" element={<Callback />} />
      <Route path="/home" element={<HomePage />} />
      <Route
        path="/referral-marketing"
        element={
          <ProtectedRoute checkAccess={canAccessRMM}>
            <ReferralMarketing />
          </ProtectedRoute>
        }
      />
      <Route
        path="/referral-marketing/:id"
        element={
          <ProtectedRoute checkAccess={canAccessRMM}>
            <RmmAutomationDetails />
          </ProtectedRoute>
        }
      />
      {/* One route per queue (abo-print-shop-menu-split). The bare
          /print-shop was the Ticket Queue and now redirects (Books by
          default). The `key`s make React mount a fresh page when the sidebar
          moves between the two ticket queues, which share a component. */}
      <Route path="/print-shop" element={<PrintShopLegacyRedirect />} />
      {/* Mail on Demand only (its queue, the mailing batches, the MOD
          pricing): mounted while VITE_ENABLE_MOD is on. Hidden, each of
          these URLs lands on the Books queue. */}
      {env.ENABLE_MOD ? (
        <Route
          path="/print-shop/mod"
          element={<PrintShopQueue key="mod" origin="mod" />}
        />
      ) : (
        <Route path="/print-shop/mod" element={<HiddenMailOnDemandRedirect />} />
      )}
      <Route
        path="/print-shop/books"
        element={<PrintShopQueue key="book" origin="book" />}
      />
      <Route path="/print-shop/rm" element={<RmPrintQueue />} />
      <Route path="/print-shop/rm/:jobId" element={<RmJobDetail />} />
      <Route
        path="/print-shop/tickets/:orderId"
        element={<PrintShopTicketDetail />}
      />
      {env.ENABLE_MOD ? (
        <>
          <Route
            path="/print-shop/pricing-config"
            element={<PrintShopPricingConfig />}
          />
          <Route path="/print-shop/batches" element={<PrintShopBatchesList />} />
          <Route
            path="/print-shop/batches/new"
            element={<PrintShopBatchBuilder />}
          />
          <Route
            path="/print-shop/batches/:batchId"
            element={<PrintShopBatchBuilder />}
          />
        </>
      ) : (
        <>
          <Route
            path="/print-shop/pricing-config"
            element={<HiddenMailOnDemandRedirect />}
          />
          <Route
            path="/print-shop/batches/*"
            element={<HiddenMailOnDemandRedirect />}
          />
        </>
      )}
    </Routes>
  );
};

export default RoutesConfig;
